import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import to from 'await-to-js'
import { MdArrowBack, MdArrowForward } from 'react-icons/md'
import { Editor } from 'components/Editor/Editor'
import { Attachment } from 'components/UI/Attachment/Attachment'
import FbButton from 'components/FbUI/FbButton'
import { EmailContact, EmailMessage } from 'models/emailIntegration'
import apiService from 'services/api'

// Skeleton building block
function Skeleton({ className }: { className?: string }) {
  return <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
}

// Header skeleton loader
function EmailHeaderSkeleton() {
  return (
    <div className="flex flex-row gap-2">
      <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
      <div className="flex flex-col justify-center gap-1 flex-1 min-w-0">
        <div className="flex flex-row items-center gap-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24 ml-auto" />
        </div>
        <Skeleton className="h-4 w-48" />
      </div>
    </div>
  )
}

// Body skeleton loader
function EmailBodySkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-4/5" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
  )
}

// Format recipients for display
function formatRecipients(recipients: EmailContact[]): string {
  return recipients
    .map((r) => (r.name ? `${r.name} (${r.email})` : r.email))
    .join(', ')
}

// Get initials from name
function getInitials(name: string): string {
  return (
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'UN'
  )
}

// Email header component
interface EmailHeaderProps {
  senderName: string
  senderEmail: string | null
  date: string
  toRecipients: EmailContact[]
  ccRecipients: EmailContact[]
}

function EmailHeader({
  senderName,
  senderEmail,
  date,
  toRecipients,
  ccRecipients,
}: EmailHeaderProps) {
  const initials = getInitials(senderName)
  const formattedDate = format(new Date(date), 'PPpp')

  return (
    <>
      <div className="flex flex-row gap-2">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-lg bg-[#C4C4C4] flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-base">{initials}</span>
        </div>

        {/* Text block */}
        <div className="flex flex-col justify-center gap-1 flex-1 min-w-0">
          {/* Line 1: Name (email) + date */}
          <div className="flex flex-row items-center gap-1">
            <span className="text-sm font-semibold leading-4 text-[#191D23] truncate">
              {senderName}
            </span>
            {senderEmail && (
              <span className="text-sm font-normal leading-4 text-[#6E6E6E] truncate">
                ({senderEmail})
              </span>
            )}
            <span className="text-xs font-normal leading-4 text-[#6E6E6E] ml-auto flex-shrink-0">
              {formattedDate}
            </span>
          </div>

          {/* Line 2: To recipients */}
          {toRecipients.length > 0 && (
            <div className="text-sm font-light leading-4 text-[#6E6E6E] truncate">
              to {formatRecipients(toRecipients)}
            </div>
          )}
        </div>
      </div>

      {/* CC recipients */}
      {ccRecipients.length > 0 && (
        <div className="text-sm font-light leading-4 text-[#6E6E6E] pl-12">
          <span className="font-normal">Cc: </span>
          {formatRecipients(ccRecipients)}
        </div>
      )}
    </>
  )
}

// Navigation buttons component
interface EmailNavigationProps {
  onPrev?: () => void
  onNext?: () => void
}

function EmailNavigation({ onPrev, onNext }: EmailNavigationProps) {
  if (!onPrev && !onNext) return null

  return (
    <div className="flex justify-between items-center mt-auto pt-4">
      <FbButton
        variant="secondary"
        onClick={onPrev}
        style={{ visibility: onPrev ? 'visible' : 'hidden' }}
      >
        <MdArrowBack />
        Previous
      </FbButton>
      <FbButton
        variant="secondary"
        onClick={onNext}
        style={{ visibility: onNext ? 'visible' : 'hidden' }}
      >
        Next
        <MdArrowForward />
      </FbButton>
    </div>
  )
}

// Main component
interface EmailDetailViewProps {
  email: EmailMessage
  onPrev?: () => void
  onNext?: () => void
}

export function EmailDetailView({
  email,
  onPrev,
  onNext,
}: EmailDetailViewProps) {
  const api = apiService()
  const [isLoading, setIsLoading] = useState(true)
  const [emailData, setEmailData] = useState<EmailMessage | null>(null)

  useEffect(() => {
    if (email?.id) {
      loadEmailContent()
    }
  }, [email?.id])

  const loadEmailContent = async () => {
    if (!email?.id) return

    setIsLoading(true)
    const [err, response] = await to(
      api.emailIntegration.getEmailMessageById(email.id)
    )

    if (err) {
      console.error('Error loading email content', err)
      setIsLoading(false)
      return
    }

    setEmailData(response)
    setIsLoading(false)
  }

  const displayData = emailData || email
  const senderInfo = displayData?.sender?.[0]
  const senderName = senderInfo?.name || 'Unknown'
  const senderEmail = senderInfo?.email || null
  const toRecipients = displayData?.to || []
  const ccRecipients = displayData?.cc || []

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header */}
      {isLoading ? (
        <EmailHeaderSkeleton />
      ) : (
        <EmailHeader
          senderName={senderName}
          senderEmail={senderEmail}
          date={email.date}
          toRecipients={toRecipients}
          ccRecipients={ccRecipients}
        />
      )}

      {/* Body */}
      <div className="overflow-y-auto p-[12px] border border-[#E2E2E2] rounded-md text-sm font-normal leading-[1.4] [&_.ql-container]:!border-0 [&_.ql-editor]:!p-0 [&_.ql-editor]:!mt-0">
        {isLoading ? (
          <EmailBodySkeleton />
        ) : (
          <Editor value={emailData?.body || ''} readOnly disableToolbar />
        )}
      </div>

      {/* Attachments */}
      {emailData?.attachments && emailData.attachments.length > 0 && (
        <div>
          <h3 className="text-md font-semibold mb-2">Attachments</h3>
          <div className="flex flex-wrap gap-2">
            {emailData.attachments.map((attachment, index) => (
              <Attachment
                key={index}
                filename={attachment.filename}
                size={attachment.size}
                contentType={attachment.content_type}
                onClick={() => {
                  /* Handle attachment download here */
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <EmailNavigation onPrev={onPrev} onNext={onNext} />
    </div>
  )
}
